import React, { useState, useEffect } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { utcNow } from 'app/common/utils/helpers'
import { connectResource } from 'app/common/utils/resource'
import { newMessage, submitMessage } from 'app/store/reducers/conversation/conversationSlice'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap'

import { NewMessageWrapper } from './NewMessageWrapper'
import { USER_TYPES } from 'app/common/constants/userTypes'
import NewMessageContainerOld from './NewMessageContainerOld'
import { useFlags } from 'launchdarkly-react-client-sdk'

const NewMessageContainer = (props) => {
  /**
  NewMessageContainer component renders UI for creating a message and handles request to backend when message is submitted by user.
  */
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recipient, setRecipient] = useState(null)

  const flags = useFlags() || {}

  // Destructure props to avoid dependency issues
  const {
    textToInsert,
    setTextToInsert,
    setSelectedTemplate,
    onViewAllTemplates,
    selectedTemplate,
    isUnifiedConversation,
  } = props

  // Set recipient based on template's audience when template is selected
  useEffect(() => {
    if (isUnifiedConversation && selectedTemplate?.audience) {
      setRecipient(selectedTemplate.audience)
    } else if (!selectedTemplate) {
      setRecipient(null)
    }
  }, [selectedTemplate, isUnifiedConversation])

  const onNewMessageSend = ({
    userCanApproveMessage = false,
    isUnifiedConversation = false,
    userCanChooseRecipient = false,
  }) => {
    const {
      currentUserID,
      currentUserInfo,
      conversation_id,
      conversationType,
      orderData,
      newMessage,
      is_revision,
      is_amc_lender,
      addVendorNote,
      messageAdd,
      submitMessage,
      selectedTemplate,
    } = props

    if (!textToInsert) {
      return
    }

    const sender_type = is_amc_lender ? USER_TYPES.AMC : USER_TYPES.LENDER

    // For users with approval permission, always use recipient from dropdown
    // Otherwise, if a template is selected, use its audience
    // Otherwise, use recipient from state or default to 'vendor'
    const recipientValue = userCanApproveMessage
      ? (recipient || 'vendor')
      : (selectedTemplate?.audience || recipient || 'vendor')

    const message = {
      message: textToInsert,
      is_revision: is_revision,
      conversation_id: conversation_id,
      order_id: orderData.id,
      sender_type: sender_type,
      conversation_type: conversationType,
      sent_time: utcNow(),
      recipient: recipientValue,
    }

    if (flags?.useUpdatedMessageTemplatesUx && selectedTemplate) {
      message.template_id = selectedTemplate.id
    }


    let promiseFunction = null
    if (isUnifiedConversation) {
      promiseFunction = () => submitMessage({ conversationId: conversation_id, messageRecord: message })
    } else {
      const namespace = conversationType === 'current_vendor_messages' ? addVendorNote : messageAdd
      promiseFunction = () => namespace.put(message)
    }
    setIsSubmitting(true)
    promiseFunction()
      .then(() => {
        newMessage({
          orderId: orderData.id,
          message: buildMessage({
            currentUserInfo,
            currentUserID,
            recipient: recipientValue,
            userCanApproveMessage,
            message,
            sender_type,
            isUnifiedConversation,
            selectedTemplate,
          }),
          conversationType,
        })
      })
      .catch(() => null)
      .finally(() => {
        setTextToInsert('')
        setModalIsOpen(false)
        setIsSubmitting(false)
        setRecipient(null)
        setSelectedTemplate(null)
      })
  }

  const buildMessage = ({
    currentUserInfo,
    currentUserID,
    recipient,
    userCanApproveMessage,
    isUnifiedConversation,
    message,
    sender_type,
    selectedTemplate,
  }) => {
    /**
    Builds message object that will be added to the store for a newly sent message.

    This means we do not need to refetch the entire conversation when a user sends a message.
    */
    let newMessage = {
      ...message,
      sender: {
        ...currentUserInfo,
        id: currentUserID,
        type: sender_type,
      },
      visible_to_vendor: true,
      visible_to_internal: true,
      requires_approval: false,
    }

    if (isUnifiedConversation) {
      newMessage = {
        ...newMessage,
        visible_to_internal: false,
        visible_to_vendor: false,
        requires_approval: false,
      }

      const isPreApprovedTemplate = !!selectedTemplate?.is_pre_approved

      if (sender_type === USER_TYPES.AMC) {
        // Is AMP Lender User
        newMessage.visible_to_vendor = true
        newMessage.requires_approval = true
      } else if (userCanApproveMessage) {
        // User can approve messages
        if (recipient === 'vendor') {
          newMessage.visible_to_vendor = true
        } else if (recipient === 'everyone') {
          newMessage.visible_to_vendor = true
          newMessage.visible_to_internal = true
        } else if (recipient === 'internal') {
          newMessage.visible_to_internal = true
        }
      } else {
        // User cannot approve messages
        if (recipient === 'vendor') {
          if (isPreApprovedTemplate) {
            newMessage.visible_to_vendor = true
            newMessage.visible_to_internal = true
            newMessage.requires_approval = false
          } else {
            newMessage.requires_approval = true
            newMessage.visible_to_internal = true
          }
        } else if (recipient === 'everyone') {
          newMessage.visible_to_vendor = true
          newMessage.visible_to_internal = true
          if (!isPreApprovedTemplate) {
            newMessage.requires_approval = true
          }
        } else if (recipient === 'internal') {
          newMessage.visible_to_internal = true
        }
      }
    }
    return newMessage
  }

  const handleInputChange = (event) => {
    setTextToInsert(event.target.value, 'manual')
  }

  const onTemplateSelect = (template) => {
    setTextToInsert(template.text, 'template')
    setSelectedTemplate(template)
  }

  const toggle = () => {
    setModalIsOpen(!modalIsOpen)
  }

  const onFocus = () => {
    const { onFocus } = props
    if (onFocus) { onFocus() }
  }

  const handleRecipientChange = (value) => {
    setRecipient(value)
  }

  const {
    modal,
    orderData,
    newMessageTitle,
    conversationType,
    conversationCompany,
    is_amc_lender,
  } = props

  if (!flags?.useUpdatedMessageTemplatesUx) {
    return <NewMessageContainerOld {...props} />
  }

  if (!modal) {
    if (orderData.has_been_reassigned) {
      // Hide the message input if the order has been reassigned
      return <></>
    } else {
      return (
        <>
          <NewMessageWrapper
            isSubmitting={isSubmitting}
            onSubmit={onNewMessageSend}
            onFocus={onFocus}
            newMessageValue={textToInsert}
            handleInputChange={handleInputChange}
            newMessageTitle={newMessageTitle}
            orderData={orderData}
            handleRecipientChange={handleRecipientChange}
            recipient={recipient}
            conversationType={conversationType}
            conversationCompany={conversationCompany}
            isAMPUser={is_amc_lender}
            selectedTemplate={selectedTemplate}
            templates={props.templates?.data?.templates || []}
            onTemplateSelect={onTemplateSelect}
            onViewAllTemplates={onViewAllTemplates} // This will be handled by the parent ChatSelector
            audienceFilter={props.audienceFilter}
            isUnifiedConversation={props.isUnifiedConversation}
            isTemplateSuggestionEnabled={props.isTemplateSuggestionEnabled}
          />
        </>
      )
    }
  }

  return (
    <div className='w-100'>
      <Button color="danger" onClick={toggle}>Request Revision</Button>
      <Modal isOpen={modalIsOpen} toggle={toggle} >
        <ModalHeader toggle={toggle}>Request Revision</ModalHeader>
        <ModalBody>
          <textarea className='w-100' type='text' value={textToInsert} onChange={handleInputChange}/>
        </ModalBody>
        <ModalFooter>
          <Button disabled={isSubmitting} color="primary" onClick={onNewMessageSend}>Send</Button>{' '}
          <Button disabled={isSubmitting} color="secondary" onClick={toggle}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

function mapStateToProps(state) {
  return {
    currentUserID: state.resource.user.data.id,
    currentUserInfo: {
      firstname: state.resource.user.data.firstname,
      lastname: state.resource.user.data.lastname,
      username: state.resource.user.data.username,
    },
  }
}

export default compose(
  connectResource({
    namespace: 'messageAdd',
    endpoint: 'order/message',
    prefetch: false,
    navigateAfterSubmit: false,
    excludeID: true,
    apiVersion: 2,
  }),
  connectResource({
    namespace: 'addVendorNote',
    endpoint: 'order/current-vendor-message',
    prefetch: false,
    navigateAfterSubmit: false,
    excludeID: true,
    apiVersion: 2,
  }),
  connect(mapStateToProps, { newMessage, submitMessage }),
)(NewMessageContainer)
